import { useMemo } from 'react';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Grid3x3, HelpCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCohortRetention, type CohortRetentionRow } from '@/hooks/useDashboardAnalytics';

interface CohortRetentionWidgetProps {
  months?: number;
  className?: string;
}

const RETENTION_MONTH_OFFSETS = [0, 1, 2, 3, 4, 5] as const;

/**
 * Градиент цвета ячейки по retention: 0% → светло-красный, 100% → насыщенный зелёный.
 * Возвращает HSL-строку для inline background.
 */
function retentionColor(rate: number): string {
  const clamped = Math.max(0, Math.min(100, rate));
  const hue = 0 + (clamped / 100) * 130; // 0 = красный, 130 ~ зелёный
  const saturation = 55 + (clamped / 100) * 15;
  const lightness = 90 - (clamped / 100) * 40;
  return `hsl(${Math.round(hue)}, ${Math.round(saturation)}%, ${Math.round(lightness)}%)`;
}

function textColor(rate: number): string {
  return rate >= 45 ? 'text-slate-50' : 'text-slate-800';
}

function formatCohortLabel(monthKey: string): string {
  const [year, month] = monthKey.split('-');
  if (!year || !month) {return monthKey;}
  const d = new Date(Number(year), Number(month) - 1, 1);
  return d.toLocaleDateString('ru-RU', { month: 'short', year: '2-digit' });
}

interface CohortRowProps {
  cohort: CohortRetentionRow;
}

function CohortRow({ cohort }: CohortRowProps) {
  return (
    <TooltipProvider delayDuration={120}>
      <div className="grid grid-cols-[96px_56px_repeat(6,1fr)] gap-1 items-stretch text-xs">
        <div className="flex items-center font-medium text-foreground px-2">
          {formatCohortLabel(cohort.month)}
        </div>
        <div className="flex items-center justify-end px-2 text-muted-foreground tabular-nums">
          {cohort.size}
        </div>
        {RETENTION_MONTH_OFFSETS.map((monthOffset) => {
          const rate = cohort.retention[monthOffset] ?? 0;
          return (
            <Tooltip key={`${cohort.month}-m${monthOffset}`}>
              <TooltipTrigger asChild>
                <div
                  className={`h-8 rounded-sm flex items-center justify-center font-medium tabular-nums ${textColor(rate)}`}
                  style={{ backgroundColor: retentionColor(rate) }}
                >
                  {rate > 0 ? `${rate}%` : '—'}
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" align="center">
                <div className="space-y-0.5">
                  <p className="font-medium">Когорта {formatCohortLabel(cohort.month)}</p>
                  <p>Месяц {monthOffset}: {rate}%</p>
                  <p className="text-muted-foreground">
                    Размер когорты: {cohort.size}
                  </p>
                </div>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}

function CohortHeader() {
  return (
    <div className="grid grid-cols-[96px_56px_repeat(6,1fr)] gap-1 text-xs text-muted-foreground px-1">
      <div className="px-2">Когорта</div>
      <div className="px-2 text-right">Размер</div>
      {RETENTION_MONTH_OFFSETS.map((monthOffset) => (
        <div key={`header-m${monthOffset}`} className="text-center">М{monthOffset}</div>
      ))}
    </div>
  );
}

function CohortRetentionGuide() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground"
          aria-label="Что значит когортное удержание"
        >
          <HelpCircle className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Когортное удержание</DialogTitle>
          <DialogDescription>
            Показывает, какая доля пользователей продолжает платить после первой оплаты.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="space-y-2">
            <p>
              <span className="font-medium text-foreground">Когорта</span>
              {' '}— месяц, в котором пользователи впервые оплатили продукт.
            </p>
            <p>
              <span className="font-medium text-foreground">Размер</span>
              {' '}— количество пользователей с первой оплатой в этом месяце.
            </p>
            <p>
              <span className="font-medium text-foreground">М0, М1, М2...</span>
              {' '}— месяцы после первой оплаты: М0 это месяц первой оплаты, М1 следующий месяц, М2 через два месяца.
            </p>
          </div>

          <div className="rounded-md border bg-muted/30 p-3">
            <p className="font-medium text-foreground">Формула ячейки</p>
            <p className="mt-1 text-muted-foreground">
              Пользователи из когорты с подтверждённой оплатой в месяце N / размер когорты * 100%.
            </p>
          </div>

          <p className="text-muted-foreground">
            Это платёжное удержание, а не посещения сайта: учитываются только платежи со статусом подтверждения.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CohortRetentionHeader() {
  return (
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-medium flex items-center gap-2">
        <Grid3x3 className="h-4 w-4" />
        Когортное удержание
      </CardTitle>
      <p className="text-xs text-muted-foreground mt-1">
        % когорты с оплатой в N-м месяце после первой
      </p>
      <CardAction>
        <CohortRetentionGuide />
      </CardAction>
    </CardHeader>
  );
}

export function CohortRetentionWidget({ months = 12, className }: CohortRetentionWidgetProps) {
  const { data, isLoading } = useCohortRetention(months);

  const cohorts = useMemo(() => data?.cohorts ?? [], [data?.cohorts]);

  if (isLoading) {
    return (
      <Card className={className}>
        <CohortRetentionHeader />
        <CardContent className="space-y-2">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={`cohort-skeleton-${i}`} className="h-8 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (cohorts.length === 0) {
    return (
      <Card className={className}>
        <CohortRetentionHeader />
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            Недостаточно данных для когортного анализа
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CohortRetentionHeader />
      <CardContent className="p-0">
        <ScrollArea className="max-h-[420px] px-4 pb-4">
          <div className="space-y-2 min-w-[560px]">
            <CohortHeader />
            {cohorts.map((cohort) => (
              <CohortRow key={cohort.month} cohort={cohort} />
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
